// src/components/crm/FunnelEditorModal.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, TextInput, Modal, Select, Spinner } from "flowbite-react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  HiOutlinePencil,
  HiOutlineBars3,
  HiTrash,
  HiCheckCircle,
  HiOutlineStar,
  HiStar,
} from "react-icons/hi2";
import axios from "../../config/axiosInstance";
import { CRM, TEAMS } from "../../config/apiConfig";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { HiExclamationCircle } from "react-icons/hi";

/* ===== helpers ===== */
const makeClientKey = () =>
  `stage_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
const withClientKeys = (stages = []) =>
  (stages || []).map((s, i) => ({
    ...s,
    clientKey: s.clientKey || makeClientKey(),
    order: s.order ?? i + 1,
  }));
const toArray = (maybe) =>
  Array.isArray(maybe)
    ? maybe
    : maybe?.rows?.length
    ? maybe.rows
    : maybe?.items?.length
    ? maybe.items
    : maybe?.data?.length
    ? maybe.data
    : [];

/* ===== sortable row ===== */
function SortableStageRow({
  id,
  stage,
  isClosing,
  onRename,
  onMarkClosing,
  onDelete,
  onOpenInspector,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  const [editing, setEditing] = useState(false);
  const [localName, setLocalName] = useState(stage?.name || "");
  const commit = () => {
    const trimmed = localName.trim();
    if (trimmed && trimmed !== stage.name) onRename(trimmed);
    setEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 hover:shadow-sm transition mb-2"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab shrink-0 text-gray-400 group-hover:text-gray-600"
          title="Arrastar"
        >
          <HiOutlineBars3 />
        </span>

        <button
          type="button"
          onClick={onMarkClosing}
          className={[
            "shrink-0 rounded-md p-1.5",
            isClosing
              ? "text-amber-600 hover:text-amber-700 bg-amber-50 border border-amber-200"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-100",
          ].join(" ")}
          title="Marcar como fechamento"
        >
          {isClosing ? <HiStar /> : <HiOutlineStar />}
        </button>

        {editing ? (
          <TextInput
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => e.key === "Enter" && commit()}
            autoFocus
            className="h-9 w-[260px]"
          />
        ) : (
          <button
            className="text-sm font-medium text-gray-900 truncate text-left"
            title="Editar etapa"
            onClick={onOpenInspector}
          >
            {stage.name}
          </button>
        )}

        <span className="ml-2 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-700">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: stage.color || "#141414" }}
          />
          {stage.probability ?? 0}%
        </span>

        {stage?.isWin && (
          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">
            <HiCheckCircle /> Ganho
          </span>
        )}
        {stage?.isLost && (
          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700">
            <HiExclamationCircle /> Perda
          </span>
        )}
      </div>

      <div className="flex items-center gap-0.5 shrink-0">
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-gray-600 hover:text-gray-800 rounded-md p-1.5 hover:bg-gray-100"
            title="Renomear rápido"
          >
            <HiOutlinePencil />
          </button>
        )}
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-600 rounded-md p-1.5 hover:bg-red-50"
          title="Excluir etapa"
        >
          <HiTrash />
        </button>
      </div>
    </div>
  );
}

/* ===== editor modal (reutilizável) ===== */
export default function FunnelEditorModal({
  open,
  onClose,
  initialFunnel,
  onSaved,
}) {
  const queryClient = useQueryClient();
  const sensors = useSensors(useSensor(PointerSensor));
  const navigate = useNavigate();

  const [funnelId, setFunnelId] = useState("");
  const [funnelName, setFunnelName] = useState("");
  const [visibility, setVisibility] = useState("PRIVATE");
  const [teamId, setTeamId] = useState("");
  const [closingStageKey, setClosingStageKey] = useState("");
  const [stages, setStages] = useState([]);
  const [newStage, setNewStage] = useState("");

  const [errorName, setErrorName] = useState("");
  const [errorStages, setErrorStages] = useState("");
  const [errorVis, setErrorVis] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteError, setDeleteError] = useState("");

  const { data: teams = [], isLoading: loadingTeams } = useQuery(
    "teams",
    async () => {
      const res = await axios.get(TEAMS.LIST);
      return toArray(res?.data?.data ?? res?.data);
    },
    { enabled: open }
  );

  useEffect(() => {
    if (!open) return;
    const f = initialFunnel || {};
    setFunnelId(f.id || "");
    setFunnelName(f.name || "");
    setVisibility(f.visibility || "PRIVATE");
    setTeamId(f.teamId || "");
    const sts = withClientKeys(toArray(f.stages || []));
    setStages(
      sts.map((s, i) =>
        typeof s === "string"
          ? {
              id: "",
              clientKey: makeClientKey(),
              name: s,
              order: i + 1,
              probability: 0,
              color: "#141414",
              isWin: false,
              isLost: false,
              autoLeadStatus: "",
              requiredFields: [],
              autoTasks: [],
              slaHours: null,
              wipLimit: null,
              webhookOnEnter: null,
            }
          : {
              ...s,
              clientKey: s.clientKey || makeClientKey(),
              order: s.order ?? i + 1,
            }
      )
    );
    // mapear closingStageId para clientKey compatível
    const closingByIdOrName = sts.find(
      (s) => (s.id || s.name) === (f.closingStageId || "")
    );
    setClosingStageKey(closingByIdOrName?.clientKey || "");
    setNewStage("");
    setErrorName("");
    setErrorStages("");
    setErrorVis("");
  }, [open, initialFunnel]);

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const list = [...stages];
    const oldIdx = list.findIndex((s) => s.clientKey === active.id);
    const newIdx = list.findIndex((s) => s.clientKey === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const [moved] = list.splice(oldIdx, 1);
    list.splice(newIdx, 0, moved);
    setStages(list.map((s, i) => ({ ...s, order: i + 1 })));
  };

  const addStage = () => {
    const name = newStage.trim();
    if (!name) return;
    const list = [...stages];
    list.push({
      id: "",
      clientKey: makeClientKey(),
      name,
      order: list.length + 1,
      probability: 0,
      color: "#141414",
      isWin: false,
      isLost: false,
      autoLeadStatus: "",
      requiredFields: [],
      autoTasks: [],
      slaHours: null,
      wipLimit: null,
      webhookOnEnter: null,
    });
    setStages(list);
    setNewStage("");
  };

  const renameStage = (clientKey, newName) =>
    setStages((prev) =>
      prev.map((s) => (s.clientKey === clientKey ? { ...s, name: newName } : s))
    );
  const deleteStage = (clientKey) => {
    const list = stages.filter((s) => s.clientKey !== clientKey);
    if (closingStageKey && closingStageKey === clientKey)
      setClosingStageKey("");
    setStages(list.map((s, i) => ({ ...s, order: i + 1 })));
  };

  const saveMut = useMutation(
    async (payload) => {
      const hasId = !!payload.id && String(payload.id).trim() !== "";
      const url = hasId ? `${CRM.FUNNELS}/${payload.id}` : CRM.FUNNELS;
      const method = hasId ? "put" : "post";
      const res = await axios[method](url, payload);
      return res.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries("funnels");
        onSaved?.(data?.data || data);
        onClose?.();
      },
    }
  );

  const handleSave = () => {
    let hasErr = false;
    if (!funnelName.trim()) {
      setErrorName("O nome do funil é obrigatório.");
      hasErr = true;
    } else setErrorName("");

    if (stages.length === 0) {
      setErrorStages("Adicione pelo menos uma etapa.");
      hasErr = true;
    } else setErrorStages("");

    if (visibility === "TEAM" && !String(teamId || "").trim()) {
      setErrorVis("Selecione um time para a visibilidade.");
      hasErr = true;
    } else setErrorVis("");

    if (hasErr) return;

    const closingStageId =
      stages.find((s) => s.clientKey === closingStageKey)?.id || null;

    const payload = {
      id: funnelId || undefined,
      name: funnelName.trim(),
      visibility,
      teamId: visibility === "TEAM" ? teamId || null : null,
      ownerUserId:
        visibility === "PRIVATE" ? initialFunnel?.ownerUserId ?? null : null,
      closingStageId,
      stages: stages.map((s, i) => ({
        id: s.id || undefined,
        name: s.name,
        order: s.order ?? i + 1,
        probability: Number.isFinite(+s.probability) ? +s.probability : 0,
        color: s.color || null,
        isWin: !!s.isWin,
        isLost: !!s.isLost,
        autoLeadStatus: s.autoLeadStatus || null,
        requiredFields: Array.isArray(s.requiredFields) ? s.requiredFields : [],
        autoTasks: Array.isArray(s.autoTasks) ? s.autoTasks : [],
        slaHours: s.slaHours ?? null,
        wipLimit: s.wipLimit ?? null,
        webhookOnEnter: s.webhookOnEnter ?? null,
      })),
    };

    saveMut.mutate(payload);
  };

  const deleteMut = useMutation(
    async (id) => {
      if (!id) throw new Error("ID do funil ausente.");
      await axios.delete(`${CRM.FUNNELS}/${id}`);
      return id;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("funnels");
        setDeleteConfirm("");
        setDeleteError("");
        navigate("/board");
        onClose?.();
      },
      onError: (error) => {
        setDeleteError(
          error?.response?.data?.message ||
            error?.message ||
            "Não foi possível deletar o funil."
        );
      },
    }
  );

  const handleDelete = () => {
    setDeleteError("");
    if (!funnelId) return;
    if ((deleteConfirm || "").trim().toUpperCase() !== "DELETAR") {
      setDeleteError('Digite "DELETAR" para confirmar a exclusão.');
      return;
    }
    deleteMut.mutate(funnelId);
  };

  return (
    <Modal show={open} onClose={onClose} size="3xl">
      <Modal.Header>
        <span className="text-lg font-bold text-gray-800">
          {funnelId ? "Editar Funil" : "Novo Funil"}
        </span>
      </Modal.Header>
      <Modal.Body>
        {/* Nome */}
        <div className="rounded-lg border border-gray-200 p-4 bg-white space-y-1">
          <label className="text-sm font-medium text-gray-700">
            Nome do Funil *
          </label>
          <TextInput
            value={funnelName}
            onChange={(e) => setFunnelName(e.target.value)}
            color={errorName ? "failure" : undefined}
            helperText={errorName && <span>{errorName}</span>}
          />
        </div>

        {/* Visibilidade */}
        <div className="rounded-lg border border-gray-200 p-4 bg-white mt-4">
          <div
            className={`gap-4 ${
              visibility === "TEAM" ? "grid sm:grid-cols-2" : "sm:grid-cols-1"
            }`}
          >
            <div>
              <label className="text-sm font-medium text-gray-700">
                Visibilidade *
              </label>
              <Select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                color={errorVis ? "failure" : undefined}
              >
                <option value="PRIVATE">Somente eu</option>
                <option value="TEAM">Um time específico</option>
                <option value="PUBLIC">Todos os usuários</option>
              </Select>
              {errorVis && (
                <p className="text-xs text-red-600 mt-1">{errorVis}</p>
              )}
            </div>

            {visibility === "TEAM" && (
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Time
                </label>
                <Select
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                  disabled={loadingTeams}
                >
                  <option value="">Selecione um time…</option>
                  {toArray(teams).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Etapas */}
        <div className="rounded-lg border border-gray-200 p-4 bg-white mt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-700">
              Etapas do Funil
            </h4>
            <span className="text-xs text-gray-500">
              Arraste para reordenar • clique em “Detalhes” para editar
            </span>
          </div>

          {stages.length > 0 && (
            <div className="bg-gray-50 rounded-md p-3 border border-gray-200 max-h-[360px] overflow-y-auto">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={stages.map((s) => s.clientKey)}
                  strategy={verticalListSortingStrategy}
                >
                  {stages.map((stage) => (
                    <SortableStageRow
                      key={stage.clientKey}
                      id={stage.clientKey}
                      stage={stage}
                      isClosing={closingStageKey === stage.clientKey}
                      onRename={(name) => renameStage(stage.clientKey, name)}
                      onMarkClosing={() => setClosingStageKey(stage.clientKey)}
                      onDelete={() => deleteStage(stage.clientKey)}
                      onOpenInspector={() => {
                        /* opcional: abrir StageInspectorDrawer aqui se quiser */
                      }}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              {errorStages && (
                <p className="text-sm text-red-500 mt-2">{errorStages}</p>
              )}
            </div>
          )}

          <div className="flex gap-2 mt-3">
            <TextInput
              value={newStage}
              onChange={(e) => setNewStage(e.target.value)}
              placeholder="Nova etapa"
              className="flex-1"
              onKeyDown={(e) => e.key === "Enter" && addStage()}
            />
            <Button size="md" onClick={addStage}>
              Adicionar
            </Button>
          </div>
        </div>
        {funnelId && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 mt-4 space-y-3">
            <div>
              <p className="text-sm font-semibold text-rose-700">
                Excluir funil
              </p>
              <p className="text-xs text-rose-500">
                Esta ação não pode ser desfeita. Digite <strong>DELETAR</strong>{" "}
                para habilitar a exclusão.
              </p>
            </div>
            <div className="space-y-1">
              <TextInput
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="Digite DELETAR para confirmar"
                color={deleteError ? "failure" : undefined}
                onKeyDown={(e) => e.key === "Enter" && handleDelete()}
              />
              {deleteError && (
                <p className="text-xs text-rose-600">{deleteError}</p>
              )}
            </div>
            <Button
              color="failure"
              onClick={handleDelete}
              disabled={deleteMut.isLoading}
              isProcessing={deleteMut.isLoading}
              className="w-full"
            >
              {deleteMut.isLoading ? "Deletando..." : "Deletar funil"}
            </Button>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="flex justify-end">
        <Button
          onClick={handleSave}
          className="w-full sm:w-auto"
          isProcessing={saveMut.isLoading}
          disabled={saveMut.isLoading}
        >
          {saveMut.isLoading ? "Salvando..." : "Salvar Funil"}
        </Button>
        <Button
          color="gray"
          onClick={onClose}
          className="w-full sm:w-auto"
          disabled={saveMut.isLoading}
        >
          Cancelar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
